import { RADII, SPACING, STROKES } from "../../../lib/brand";
import { PANE_THEMES } from "../../../components/brand/paneThemes";
import { useFocusEffect } from "expo-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Platform, Pressable, ScrollView, Text, View } from "react-native";

import type { AppRole } from "../../../lib/auth";
import { listAllUsers, setUserRole, type AdminUserRow } from "../../../lib/admin";
import { useAuth } from "../../../lib/authContext";
import { exportRowsAsCsv, fmtCellDate, todayDateForFilename } from "../../../lib/csvExport";
import { enterImpersonation } from "../../../lib/impersonate";
import { useAdminListFilter } from "../../../lib/useAdminListFilter";
import {
  AdminButton,
  AdminEmpty,
  AdminError,
  AdminInput,
  AdminLoading,
  AdminPageHeader,
  FilterChip,
} from "../../../components/admin/ui";
import { StatusPill } from "../../../components/aspects/StatusPill";

const THEME = PANE_THEMES.Admin;

const ROLE_OPTIONS: { label: string; value: AppRole | null }[] = [
  { label: "No role", value: null },
  { label: "student", value: "student" },
  { label: "client", value: "client" },
  { label: "judge", value: "judge" as unknown as AppRole },
  { label: "admin_licensing", value: "admin_licensing" },
  { label: "admin_rm", value: "admin_rm" },
  { label: "super_admin", value: "super_admin" },
];

type RoleFilter = "all" | "none" | AppRole | "judge";
type SortKey = "recent_signin" | "recent_created" | "alpha";

const ROLE_FILTERS: { label: string; value: RoleFilter }[] = [
  { label: "All", value: "all" },
  { label: "No role", value: "none" },
  { label: "Student", value: "student" },
  { label: "Client", value: "client" },
  { label: "Judge", value: "judge" },
  { label: "Admin (licensing)", value: "admin_licensing" },
  { label: "Admin (RM)", value: "admin_rm" },
  { label: "Super admin", value: "super_admin" },
];

const SORT_LABELS: Record<SortKey, string> = {
  recent_signin: "Recently signed up",
  recent_created: "Recently created",
  alpha: "Alphabetical",
};

export default function AdminUsersScreen() {
  const { user: authUser, role: myRole } = useAuth();
  const canViewAs = myRole === "super_admin";
  const [rows, setRows] = useState<AdminUserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState<AppRole | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const hasLoadedRef = useRef(false);

  const filter = useAdminListFilter({
    keys: ["q", "role", "sort"],
    defaults: { q: "", role: "all", sort: "recent_signin" },
  });
  const search = filter.get("q");
  const roleFilter = filter.get("role") as RoleFilter;
  const sortKey = filter.get("sort") as SortKey;

  const [searchInput, setSearchInput] = useState(search);
  useEffect(() => setSearchInput(search), [search]);
  useEffect(() => {
    const t = setTimeout(() => {
      if (searchInput !== search) filter.set({ q: searchInput });
    }, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const load = useCallback(async () => {
    if (!hasLoadedRef.current) setLoading(true);
    setErr(null);
    try {
      setRows(await listAllUsers());
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to load users");
    } finally {
      hasLoadedRef.current = true;
      setLoading(false);
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      void load();
    }, [load])
  );

  async function onSaveRole(userId: string) {
    setSavingId(userId);
    try {
      await setUserRole(userId, selectedRole);
      await load();
      setExpandedUserId(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to save role");
    } finally {
      setSavingId(null);
    }
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = rows.filter((r) => {
      if (roleFilter === "none") {
        if (r.roles.length > 0) return false;
      } else if (roleFilter !== "all") {
        if (!r.roles.includes(roleFilter as AppRole)) return false;
      }
      if (q) {
        const hay = `${r.full_name ?? ""} ${r.email ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    list = [...list].sort((a, b) => {
      if (sortKey === "alpha") {
        return (a.full_name ?? a.email ?? "").localeCompare(b.full_name ?? b.email ?? "");
      }
      const at = a.signed_up_at ? new Date(a.signed_up_at).getTime() : 0;
      const bt = b.signed_up_at ? new Date(b.signed_up_at).getTime() : 0;
      return bt - at;
    });
    return list;
  }, [rows, search, roleFilter, sortKey]);

  return (
    <ScrollView style={{ flex: 1, backgroundColor: THEME.pageBg }} contentContainerStyle={{ paddingBottom: 32 }}>
      <View style={{ padding: SPACING.lg, maxWidth: 1080 }}>
        <AdminPageHeader
          theme={THEME}
          eyebrow="Platform · Users"
          title="All users + roles"
          subtitle="Every account on the platform with their assigned role."
          actions={
            <AdminButton
              theme={THEME}
              label="Export CSV"
              variant="ghost"
              size="sm"
              onPress={() =>
                exportRowsAsCsv({
                  filename: `all_users_${todayDateForFilename()}.csv`,
                  auditPage: "users",
                  columns: [
                    { header: "User ID", get: (r: AdminUserRow) => r.id },
                    { header: "Full name", get: (r: AdminUserRow) => r.full_name ?? "" },
                    { header: "Email", get: (r: AdminUserRow) => r.email ?? "" },
                    { header: "Roles", get: (r: AdminUserRow) => r.roles.join("|") },
                    { header: "Signed up", get: (r: AdminUserRow) => fmtCellDate(r.signed_up_at) },
                  ],
                  rows: filtered,
                })
              }
            />
          }
        />

        {err ? <View style={{ marginBottom: SPACING.md }}><AdminError theme={THEME} message={err} onRetry={() => void load()} /></View> : null}

        {/* Filter bar */}
        <View style={{ padding: 12, borderRadius: RADII.md, backgroundColor: THEME.surfaceRaisedBg, borderWidth: STROKES.hairline, borderColor: THEME.border, gap: 10 }}>
          <AdminInput theme={THEME} value={searchInput} onChangeText={setSearchInput} placeholder="Search by name or email…" />
          <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 6 }}>
            {ROLE_FILTERS.map((opt) => (
              <FilterChip key={opt.value} theme={THEME} label={opt.label} active={roleFilter === opt.value} onPress={() => filter.set({ role: opt.value })} />
            ))}
          </View>
          <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
            <Text style={{ fontFamily: THEME.bodyMediumFont, fontSize: 11, color: THEME.textOnSurfaceDim }}>Sort:</Text>
            {(Object.keys(SORT_LABELS) as SortKey[]).map((k) => (
              <FilterChip key={k} theme={THEME} label={SORT_LABELS[k]} active={sortKey === k} onPress={() => filter.set({ sort: k })} />
            ))}
          </View>
        </View>

        <View style={{ marginTop: SPACING.md }}>
          {loading ? (
            <AdminLoading theme={THEME} label="Loading users…" />
          ) : filtered.length === 0 ? (
            <AdminEmpty theme={THEME} title="No users match these filters." />
          ) : (
            <View style={{ gap: 8 }}>
              {filtered.map((row) => {
                const expanded = expandedUserId === row.id;
                const noRole = row.roles.length === 0;
                return (
                  <View
                    key={row.id}
                    style={{
                      padding: 14,
                      borderRadius: RADII.md,
                      backgroundColor: THEME.surfaceRaisedBg,
                      borderWidth: STROKES.hairline,
                      borderColor: expanded ? THEME.accentBorderHover : THEME.border,
                    }}
                  >
                    <Pressable
                      onPress={() => {
                        setExpandedUserId((cur) => (cur === row.id ? null : row.id));
                        setSelectedRole(row.roles[0] ?? null);
                      }}
                      style={Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null}
                    >
                      <View style={{ flexDirection: "row", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <Text style={{ fontFamily: THEME.bodyMediumFont, fontSize: 14, color: THEME.textOnSurface }}>
                          {row.full_name ?? "User"}
                        </Text>
                        {noRole ? <StatusPill theme={THEME} label="Needs role" tone="amber" showDot={false} /> : null}
                      </View>
                      <Text style={{ marginTop: 2, fontFamily: THEME.bodyFont, fontSize: 11.5, color: THEME.textOnSurfaceDim }}>
                        {row.email ?? "—"}
                      </Text>
                      <Text style={{ marginTop: 2, fontFamily: THEME.bodyFont, fontSize: 11, color: THEME.textOnSurfaceFaint }}>
                        {row.roles.length ? row.roles.join(", ") : "no role"}
                      </Text>
                    </Pressable>
                    {expanded ? (
                      <View style={{ marginTop: 12, paddingTop: 12, borderTopWidth: STROKES.hairline, borderTopColor: THEME.border }}>
                        <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 6 }}>
                          {ROLE_OPTIONS.map((opt) => (
                            <FilterChip key={opt.label} theme={THEME} label={opt.label} active={selectedRole === opt.value} onPress={() => setSelectedRole(opt.value)} />
                          ))}
                        </View>
                        <View style={{ marginTop: 12, flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
                          <AdminButton theme={THEME} label="Save role" loading={savingId === row.id} onPress={() => void onSaveRole(row.id)} />
                          {canViewAs && row.id !== authUser?.id ? (
                            <AdminButton
                              theme={THEME}
                              label="View as →"
                              variant="outline"
                              onPress={() => void enterImpersonation({ userId: row.id, email: row.email })}
                            />
                          ) : null}
                        </View>
                      </View>
                    ) : null}
                  </View>
                );
              })}
            </View>
          )}
        </View>
      </View>
    </ScrollView>
  );
}
